<template>
  <div class="skill-manage-page">
    <header class="page-hero">
      <div>
        <span class="eyebrow">Skill Operations</span>
        <h2>Skill 管理</h2>
        <p>统一管理官方公开 Skill 与用户发布 Skill 的内容、权限和展示素材。</p>
      </div>
      <div class="hero-stats">
        <div><strong>{{ stats.total }}</strong><span>全部 Skill</span></div>
        <div><strong>{{ stats.official }}</strong><span>官方</span></div>
        <div><strong>{{ stats.user }}</strong><span>用户发布</span></div>
        <div><strong>{{ stats.featured }}</strong><span>推荐</span></div>
      </div>
    </header>

    <section class="content-panel">
      <div class="toolbar">
        <div class="toolbar-left">
          <el-segmented v-model="sourceFilter" :options="sourceOptions" @change="applyFilters" />
          <el-select v-model="statusFilter" class="status-select" @change="applyFilters">
            <el-option label="全部状态" value="all" />
            <el-option label="已公开" value="published" />
            <el-option label="已隐藏" value="offline" />
            <el-option label="草稿" value="draft" />
            <el-option label="审核中" value="reviewing" />
          </el-select>
          <el-select v-model="featuredFilter" class="featured-select" @change="applyFilters">
            <el-option label="全部推荐状态" value="all" />
            <el-option label="仅推荐" value="1" />
            <el-option label="未推荐" value="0" />
          </el-select>
          <el-input v-model="keyword" clearable class="keyword-input" placeholder="搜索名称、作者或 Skill UUID"
            @keyup.enter="applyFilters" @clear="applyFilters">
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>
          <el-select v-model="categoryFilter" clearable class="featured-select" placeholder="全部分类" @change="applyFilters">
            <el-option v-for="category in categories" :key="category.id" :label="category.name" :value="Number(category.id)" />
          </el-select>
        </div>
        <el-button class="ghost-btn" :loading="loading" @click="loadSkills">
          <el-icon><Refresh /></el-icon>刷新
        </el-button>
      </div>

      <div v-if="loading" class="skill-grid">
        <el-skeleton v-for="item in 8" :key="item" animated class="skill-skeleton">
          <template #template>
            <el-skeleton-item variant="image" class="skeleton-cover" />
            <div class="skeleton-body"><el-skeleton-item variant="h3" /><el-skeleton-item variant="text" /></div>
          </template>
        </el-skeleton>
      </div>
      <el-empty v-else-if="!skills.length" description="暂无符合条件的 Skill" />
      <div v-else class="skill-grid">
        <article v-for="item in skills" :key="item.id" class="skill-card">
          <div class="cover-box">
            <video v-if="item.cover_type === 'video' && item.preview_url" :src="assetUrl(item.display_preview_url || item.preview_url)"
              :poster="assetUrl(item.display_cover_url || item.cover_url)" muted preload="metadata" />
            <el-image v-else :src="assetUrl(item.display_cover_url || item.cover_url)" fit="cover" :preview-src-list="item.cover_url ? [assetUrl(item.display_cover_url || item.cover_url)] : []"
              preview-teleported>
              <template #error><div class="cover-empty"><el-icon><Picture /></el-icon><span>暂无封面</span></div></template>
            </el-image>
            <div class="cover-badges">
              <span class="source-badge" :class="item.source_type">{{ item.source_type === 'official' ? '官方' : '用户' }}</span>
              <span v-if="item.is_featured" class="recommend-badge">推荐</span>
            </div>
            <span class="media-badge">{{ item.cover_type === 'video' ? '视频预览' : '图片封面' }}</span>
          </div>
          <div class="skill-body">
            <div class="title-row"><h3>{{ item.title }}</h3><el-tag size="small" :type="statusTag(item)">{{ statusText(item) }}</el-tag></div>
            <p class="summary">{{ item.summary || '暂无简介' }}</p>
            <el-tag v-if="item.share_scope === 'team'" size="small" type="warning">仅团队 · {{ item.team_domain }}</el-tag>
            <div class="author-row">
              <el-avatar :size="24" :src="avatarUrl(item.owner_avatar)">{{ item.author_name?.slice(0, 1) }}</el-avatar>
              <span>{{ item.author_name }}</span><i>·</i><span>{{ item.category_name || '未分类' }}</span>
            </div>
            <div class="permission-row">
              <span :class="{ on: item.public_prompt }"><el-icon><Document /></el-icon>{{ item.public_prompt ? '公开指令' : '隐藏指令' }}</span>
              <span v-if="item.has_canvas" :class="{ on: item.public_canvas }"><el-icon><View /></el-icon>{{ item.public_canvas ? '开放画布' : '关闭画布' }}</span>
            </div>
            <div class="card-footer">
              <span>使用 {{ item.usage_count || 0 }} · 收藏 {{ item.favorite_count || 0 }}</span>
              <div class="card-actions">
                <el-button class="edit-btn" size="small" @click="openEdit(item)"><el-icon><Edit /></el-icon>管理</el-button>
                <el-button class="delete-btn" size="small" :loading="deletingId === item.id" @click="deleteSkill(item)"><el-icon><Delete /></el-icon>删除</el-button>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div class="pager-row">
        <el-pagination v-model:current-page="page" v-model:page-size="pageSize" :total="total"
          layout="total, sizes, prev, pager, next, jumper" :page-sizes="[8, 12, 24, 48]"
          @size-change="changePageSize" @current-change="loadSkills" />
      </div>
    </section>

    <el-drawer v-model="drawerVisible" title="管理 Skill" size="720px" class="skill-drawer" destroy-on-close>
      <div class="drawer-content">
        <div class="drawer-heading">
          <div class="drawer-preview">
            <video v-if="form.cover_type === 'video' && previewVideo" :src="assetUrl(previewVideo)" :poster="assetUrl(previewCover)" controls />
            <el-image v-else :src="assetUrl(previewCover)" fit="cover"><template #error><div class="cover-empty">暂无封面</div></template></el-image>
          </div>
          <div><span>{{ form.source_type === 'official' ? '官方 Skill' : '用户发布 Skill' }}</span><h3>{{ form.title }}</h3><p>UUID：{{ form.uuid }}</p></div>
        </div>

        <el-form label-position="top" class="edit-form">
          <div class="form-grid">
            <el-form-item label="Skill 名称"><el-input v-model="form.title" maxlength="120" show-word-limit /></el-form-item>
            <el-form-item label="公开状态">
              <el-select v-model="form.status">
                <el-option label="已公开" value="published" /><el-option label="已隐藏" value="offline" />
                <el-option label="草稿" value="draft" /><el-option label="审核中" value="reviewing" />
                <el-option label="已归档" value="archived" />
              </el-select>
            </el-form-item>
          </div>
          <el-form-item label="简介"><el-input v-model="form.summary" type="textarea" :rows="3" maxlength="500" show-word-limit /></el-form-item>
          <el-alert v-if="form.share_scope === 'team'" :title="`仅团队可见：${form.team_domain}，不会进入平台广场`" type="warning" :closable="false" />
          <el-form-item label="Skill 分类">
            <el-select v-model="form.category_id" clearable placeholder="未分类" style="width: 100%">
              <el-option v-for="category in categories" :key="category.id" :label="`${category.name}${Number(category.status) === 1 ? '' : '（已停用）'}`" :value="Number(category.id)" />
            </el-select>
            <small style="color: #8491a6">用户端按此分类展示；“首页推荐”由下方开关单独控制，停用分类不显示为用户端分类入口。</small>
          </el-form-item>

          <div class="setting-panel">
            <div><strong>展示与权限</strong><span>控制 Agent 广场中的可见内容和推荐状态</span></div>
            <div class="setting-item"><label><b>{{ form.share_scope === 'team' ? '团队展示' : '公开展示' }}</b><small>关闭后不再显示此 Skill</small></label><el-switch v-model="isPublic" /></div>
            <div class="setting-item"><label><b>首页推荐</b><small>团队 Skill 不参与平台推荐</small></label><el-switch v-model="form.is_featured" :disabled="form.share_scope === 'team'" /></div>
            <div class="setting-item"><label><b>公开 SKILL.md 指令</b><small>关闭后其他用户仍可使用，但无法查看指令正文</small></label><el-switch v-model="form.public_prompt" /></div>
            <div class="setting-item" :class="{ disabled: !form.has_canvas }"><label><b>允许查看画布</b><small>{{ form.has_canvas ? '访客可只读进入已分享画布' : '该 Skill 没有可公开的画布快照' }}</small></label><el-switch v-model="form.public_canvas" :disabled="!form.has_canvas" /></div>
          </div>

          <el-form-item label="SKILL.md 指令">
            <el-input v-model="form.system_prompt" type="textarea" :rows="13" resize="vertical" placeholder="输入完整 Skill 执行指令" />
          </el-form-item>

          <div class="media-section">
            <div class="media-title"><strong>展示素材</strong><el-radio-group v-model="form.cover_type" size="small"><el-radio-button label="image">图片</el-radio-button><el-radio-button label="video">视频</el-radio-button></el-radio-group></div>
            <div class="upload-grid">
              <el-upload action="#" :auto-upload="false" :show-file-list="false" accept="image/jpeg,image/png,image/webp" :on-change="selectCover">
                <div class="upload-box"><el-icon><Upload /></el-icon><b>更换封面图片</b><span>JPG、PNG、WEBP，建议 16:9</span></div>
              </el-upload>
              <el-upload action="#" :auto-upload="false" :show-file-list="false" accept="video/mp4,video/webm,video/quicktime" :on-change="selectVideo">
                <div class="upload-box"><el-icon><VideoCamera /></el-icon><b>更换预览视频</b><span>MP4、WEBM、MOV</span></div>
              </el-upload>
            </div>
            <el-collapse class="url-collapse">
              <el-collapse-item title="手动填写素材地址" name="url">
                <el-form-item label="封面地址"><el-input v-model="form.cover_url" /></el-form-item>
                <el-form-item label="预览视频地址"><el-input v-model="form.preview_url" /></el-form-item>
              </el-collapse-item>
            </el-collapse>
          </div>
        </el-form>
      </div>
      <template #footer>
        <div class="drawer-footer">
          <el-button class="drawer-delete-btn" type="danger" plain :loading="deletingId === form.id" @click="deleteSkill(form)"><el-icon><Delete /></el-icon>删除 Skill</el-button>
          <div><el-button @click="drawerVisible = false">取消</el-button><el-button type="primary" :loading="saving" @click="saveSkill">保存修改</el-button></div>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Document, Edit, Picture, Refresh, Search, Upload, VideoCamera, View } from '@element-plus/icons-vue'
import { API_URL, COSURL, upfile } from '@/util/urls'

const loading = ref(false)
const saving = ref(false)
const deletingId = ref(0)
const drawerVisible = ref(false)
const skills = ref([])
const total = ref(0)
const page = ref(1)
const pageSize = ref(12)
const sourceFilter = ref('all')
const statusFilter = ref('all')
const featuredFilter = ref('all')
const categoryFilter = ref('')
const categories = ref([])
const keyword = ref('')
const stats = reactive({ total: 0, official: 0, user: 0, public: 0, featured: 0 })
const sourceOptions = [{ label: '全部', value: 'all' }, { label: '官方 Skill', value: 'official' }, { label: '用户发布', value: 'user' }]
const coverFile = ref(null)
const videoFile = ref(null)
const localCover = ref('')
const localVideo = ref('')
const form = reactive({ id: 0, uuid: '', title: '', summary: '', status: 'offline', visibility: 'private', is_featured: false, public_prompt: false, public_canvas: false, has_canvas: false, system_prompt: '', cover_url: '', preview_url: '', cover_type: 'image', source_type: 'official' })
const isPublic = computed({ get: () => form.status === 'published' && (form.visibility === 'public' || form.share_scope === 'team'), set: value => { form.status = value ? 'published' : 'offline'; form.visibility = value && form.share_scope !== 'team' ? 'public' : 'private' } })
const previewCover = computed(() => localCover.value || form.display_cover_url || form.cover_url)
const previewVideo = computed(() => localVideo.value || form.display_preview_url || form.preview_url)

const isActuallyPublic = item => item?.status === 'published' && item?.visibility === 'public'
const statusText = item => item?.share_scope === 'team' && item?.status === 'published' ? '团队可见' : isActuallyPublic(item) ? '已公开' : ({ published: '仅自己可见', offline: '已隐藏', draft: '草稿', reviewing: '审核中', archived: '已归档' }[item?.status] || item?.status)
const statusTag = item => isActuallyPublic(item) ? 'success' : ({ published: 'info', offline: 'info', draft: 'warning', reviewing: 'primary', archived: 'info' }[item?.status] || 'info')
const assetUrl = value => {
  const url = String(value || '').trim()
  if (!url || /^(https?:)?\/\//i.test(url) || /^(data|blob):/i.test(url)) return url
  if (url.startsWith('/getfile/') || url.startsWith('/api/getfile/')) return `${API_URL}${url}`
  if (url.startsWith('/images/')) return `${API_URL}/getfile${url}`
  return `${API_URL}/getfile/${url.replace(/^\/+/, '')}`
}
const avatarUrl = avatar => {
  const value = String(avatar || '').trim()
  if (!value) return ''
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:')) return value
  if (value.startsWith('/')) return assetUrl(value)
  return `${API_URL}/api/getfile?filename=${encodeURIComponent(value)}`
}

const loadSkills = async () => {
  loading.value = true
  try {
    const { data } = await axios.get(`${API_URL}/admin/skills`, { params: { page: page.value, pageSize: pageSize.value, source: sourceFilter.value, status: statusFilter.value, featured: featuredFilter.value, category_id: categoryFilter.value || undefined, keyword: keyword.value.trim() } })
    if (data.msg !== 'success') throw new Error(data.error || '加载失败')
    skills.value = data.data.list || []
    total.value = Number(data.data.total || 0)
    Object.assign(stats, data.data.stats || {})
  } catch (error) { ElMessage.error(error.response?.data?.error || error.message || '加载 Skill 失败') } finally { loading.value = false }
}
const applyFilters = () => { page.value = 1; loadSkills() }
const loadCategories = async () => {
  try {
    const { data } = await axios.get(`${API_URL}/admin/skills/categories`)
    if (data.msg !== 'success') throw new Error(data.error || '加载分类失败')
    categories.value = data.data || []
  } catch (error) { ElMessage.error(error.response?.data?.error || error.message || '加载分类失败') }
}
const changePageSize = () => { page.value = 1; loadSkills() }
const revokeLocal = () => { if (localCover.value) URL.revokeObjectURL(localCover.value); if (localVideo.value) URL.revokeObjectURL(localVideo.value); localCover.value = ''; localVideo.value = '' }
const openEdit = item => {
  revokeLocal(); coverFile.value = null; videoFile.value = null
  Object.assign(form, { ...item, is_featured: Boolean(item.is_featured), public_prompt: Boolean(item.public_prompt), public_canvas: Boolean(item.public_canvas), has_canvas: Boolean(item.has_canvas), system_prompt: item.system_prompt || '', cover_type: item.cover_type || 'image' })
  form.category_id = item.category_id ? Number(item.category_id) : null
  drawerVisible.value = true
}
const selectCover = upload => { if (!upload.raw) return; coverFile.value = upload.raw; if (localCover.value) URL.revokeObjectURL(localCover.value); localCover.value = URL.createObjectURL(upload.raw); form.cover_type = 'image' }
const selectVideo = async upload => {
  if (!upload.raw) return
  videoFile.value = upload.raw
  if (localVideo.value) URL.revokeObjectURL(localVideo.value)
  localVideo.value = URL.createObjectURL(upload.raw)
  form.cover_type = 'video'
  if (!coverFile.value) {
    try { const blob = await upfile.getVideoCover(localVideo.value); coverFile.value = new File([blob], `skill_${form.id}_cover.jpg`, { type: 'image/jpeg' }); if (localCover.value) URL.revokeObjectURL(localCover.value); localCover.value = URL.createObjectURL(blob) } catch (_) { ElMessage.warning('未能自动截取视频封面，可手动上传封面') }
  }
}
const uploadMedia = async (file, type) => {
  const token = await upfile.getuptoken(localStorage.getItem('userPhone') || 'admin', type)
  const result = await upfile.uploadFile(token, file)
  return `${COSURL}/${result.key}`
}
const saveSkill = async () => {
  if (!form.title.trim()) return ElMessage.warning('请输入 Skill 名称')
  saving.value = true
  try {
    if (coverFile.value) form.cover_url = await uploadMedia(coverFile.value, 'image')
    if (videoFile.value) form.preview_url = await uploadMedia(videoFile.value, 'video')
    await axios.put(`${API_URL}/admin/skills/${form.id}`, { title: form.title.trim(), category_id: form.category_id || null, summary: form.summary.trim(), status: form.status, visibility: form.visibility, is_featured: form.is_featured, public_prompt: form.public_prompt, public_canvas: form.has_canvas && form.public_canvas, system_prompt: form.system_prompt, cover_url: form.cover_url, preview_url: form.preview_url, cover_type: form.cover_type })
    ElMessage.success('Skill 设置已保存'); drawerVisible.value = false; revokeLocal(); loadSkills()
  } catch (error) { ElMessage.error(error.response?.data?.error || error.message || '保存失败') } finally { saving.value = false }
}
const deleteSkill = async item => {
  if (!item?.id || deletingId.value) return
  try {
    await ElMessageBox.confirm(
      `确定删除“${item.title || '该 Skill'}”吗？删除后将从 Skill 列表和广场隐藏，相关分享也会下线。`,
      '删除 Skill',
      { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning', confirmButtonClass: 'el-button--danger' }
    )
  } catch (action) {
    if (action === 'cancel' || action === 'close') return
    throw action
  }

  deletingId.value = item.id
  try {
    const { data } = await axios.delete(`${API_URL}/admin/skills/${item.id}`)
    if (data.msg !== 'success') throw new Error(data.error || '删除失败')
    if (drawerVisible.value && form.id === item.id) { drawerVisible.value = false; revokeLocal() }
    ElMessage.success('Skill 已删除')
    if (skills.value.length === 1 && page.value > 1) page.value -= 1
    await loadSkills()
  } catch (error) {
    ElMessage.error(error.response?.data?.error || error.message || '删除失败')
  } finally {
    deletingId.value = 0
  }
}

loadSkills()
loadCategories()
</script>

<style lang="scss" scoped>
.skill-manage-page{height:100%;min-height:0;overflow:hidden;box-sizing:border-box;padding:24px;background:#070b12;color:#e8edf7;display:flex;flex-direction:column}.page-hero{flex-shrink:0;display:flex;justify-content:space-between;align-items:flex-end;gap:24px;margin-bottom:22px}.eyebrow{color:#ff8a38;font-size:12px;font-weight:700;letter-spacing:.16em;text-transform:uppercase}.page-hero h2{margin:7px 0 5px;font-size:25px}.page-hero p{margin:0;color:#8491a6;font-size:13px}.hero-stats{display:flex;gap:10px}.hero-stats div{min-width:86px;padding:10px 14px;border:1px solid #202b3d;border-radius:9px;background:#0f1725}.hero-stats strong,.hero-stats span{display:block}.hero-stats strong{font-size:20px;color:#fff}.hero-stats span{margin-top:3px;color:#76849a;font-size:11px}.content-panel{min-height:0;flex:1;padding:18px 18px 14px;border:1px solid #202938;border-radius:12px;background:#0c121d;overflow:auto}.toolbar{display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:18px}.toolbar-left{display:flex;align-items:center;gap:10px;flex-wrap:wrap}.status-select{width:120px}.featured-select{width:145px}.keyword-input{width:280px}.ghost-btn,.edit-btn{border-color:#314059;background:#121a28;color:#d6deea}.ghost-btn:hover,.edit-btn:hover{border-color:#ff7417;color:#ff9b4a}.skill-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(300px,1fr));gap:14px}.skill-card{overflow:hidden;border:1px solid #202b3b;border-radius:10px;background:#101827;transition:.18s}.skill-card:hover{transform:translateY(-2px);border-color:#3b4a63}.cover-box{position:relative;aspect-ratio:16/8.2;background:#080d15}.cover-box video,.cover-box>.el-image{width:100%;height:100%;display:block;object-fit:cover}.cover-empty{width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;color:#59677b;background:#0b111c}.cover-empty .el-icon{font-size:28px}.cover-badges{position:absolute;top:10px;left:10px;right:10px;display:flex;justify-content:space-between}.source-badge,.recommend-badge,.media-badge{padding:4px 8px;border-radius:5px;color:#fff;font-size:11px}.source-badge.official{background:#2563eb}.source-badge.user{background:#7c3aed}.recommend-badge{background:#f59e0b}.media-badge{position:absolute;right:10px;bottom:10px;background:rgba(5,10,18,.72)}.skill-body{padding:13px}.title-row{display:flex;align-items:center;justify-content:space-between;gap:10px}.title-row h3{min-width:0;margin:0;color:#f5f7fb;font-size:15px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.summary{height:40px;margin:8px 0 10px;color:#aab5c7;font-size:12px;line-height:1.65;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}.author-row{display:flex;align-items:center;gap:6px;color:#7f8ba0;font-size:11px}.author-row i{font-style:normal}.permission-row{display:flex;gap:8px;margin:12px 0}.permission-row span{display:flex;align-items:center;gap:4px;padding:5px 7px;border:1px solid #283347;border-radius:5px;color:#718096;font-size:11px}.permission-row span.on{border-color:rgba(34,197,94,.28);color:#71d696;background:rgba(34,197,94,.07)}.card-footer{display:flex;align-items:center;justify-content:space-between;padding-top:10px;border-top:1px solid #202b3b;color:#66758b;font-size:11px}.pager-row{display:flex;justify-content:flex-end;padding-top:18px}.skill-skeleton{overflow:hidden;border:1px solid #202b3b;border-radius:10px;background:#101827}.skeleton-cover{width:100%;height:155px}.skeleton-body{padding:14px}.drawer-content{padding:0 4px 30px}.drawer-heading{display:grid;grid-template-columns:180px 1fr;gap:16px;align-items:center;padding:14px;border:1px solid #263247;border-radius:10px;background:#0d1522}.drawer-preview{height:102px;overflow:hidden;border-radius:7px;background:#080d15}.drawer-preview video,.drawer-preview .el-image{width:100%;height:100%;object-fit:cover}.drawer-heading span{color:#ff9b4a;font-size:11px}.drawer-heading h3{margin:7px 0;color:#fff}.drawer-heading p{margin:0;color:#728097;font-size:11px}.edit-form{margin-top:18px}.form-grid{display:grid;grid-template-columns:1fr 190px;gap:12px}.setting-panel{margin:2px 0 18px;padding:15px;border:1px solid #283448;border-radius:10px;background:#0e1623}.setting-panel>div:first-child{margin-bottom:8px}.setting-panel strong,.setting-panel span{display:block}.setting-panel span{margin-top:4px;color:#748198;font-size:11px}.setting-item{display:flex;align-items:center;justify-content:space-between;padding:11px 0;border-top:1px solid #222d3e}.setting-item label b,.setting-item label small{display:block}.setting-item label b{color:#dce4f0;font-size:13px}.setting-item label small{margin-top:3px;color:#738097;font-size:11px}.setting-item.disabled{opacity:.55}.media-section{padding:15px;border:1px solid #283448;border-radius:10px;background:#0e1623}.media-title{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}.upload-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.upload-grid>.el-upload,.upload-grid :deep(.el-upload){width:100%}.upload-box{min-height:96px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;border:1px dashed #35445d;border-radius:8px;color:#d9e1ec;cursor:pointer}.upload-box:hover{border-color:#ff7417;background:rgba(255,116,23,.04)}.upload-box .el-icon{color:#ff9b4a;font-size:23px}.upload-box b{font-size:12px}.upload-box span{color:#6f7d93;font-size:10px}.url-collapse{margin-top:10px}.drawer-footer{display:flex;justify-content:flex-end;gap:8px}:deep(.el-segmented){--el-segmented-bg-color:#151d2b;--el-segmented-item-selected-bg-color:rgba(255,116,23,.18);--el-segmented-item-selected-color:#ff9b4a}:deep(.el-input__wrapper),:deep(.el-select__wrapper),:deep(.el-textarea__inner){background:#121a28;border:1px solid #2b374b;box-shadow:none;color:#e8edf7}:deep(.el-form-item__label){color:#cbd5e1;font-weight:600}:deep(.el-input__inner),:deep(.el-textarea__inner),:deep(.el-select__selected-item){color:#e8edf7}:deep(.el-pagination){--el-pagination-bg-color:#121a28;--el-pagination-hover-color:#ff9b4a;--el-pagination-text-color:#cbd5e1}:global(.el-main:has(.skill-manage-page)){height:calc(100vh - 60px);padding:0;overflow:hidden;background:#070b12}:global(.skill-drawer){background:#0a101a;color:#e8edf7}:global(.skill-drawer .el-drawer__header){margin-bottom:0;padding-bottom:16px;border-bottom:1px solid #202b3b;color:#fff}:global(.skill-drawer .el-drawer__footer){border-top:1px solid #202b3b}@media(max-width:1200px){.hero-stats div:nth-child(2),.hero-stats div:nth-child(3){display:none}.keyword-input{width:220px}}
.card-actions{display:flex;align-items:center;gap:6px}.card-actions .el-button+.el-button{margin-left:0}.delete-btn{border-color:rgba(239,68,68,.38);background:rgba(127,29,29,.12);color:#f87171}.delete-btn:hover{border-color:#ef4444!important;background:rgba(127,29,29,.25)!important;color:#fca5a5!important}.drawer-footer{justify-content:space-between;align-items:center}.drawer-footer>div{display:flex;gap:8px}.drawer-delete-btn{margin-right:auto}
</style>

<!-- 抽屉通过 Teleport 挂到 body，必须使用非 scoped 样式覆盖 Element Plus 默认白色主题。 -->
<style lang="scss">
.el-overlay .el-drawer.skill-drawer {
  --el-bg-color: #0a101a;
  --el-bg-color-overlay: #0f1725;
  --el-fill-color-blank: #0a101a;
  --el-text-color-primary: #eef3fb;
  --el-text-color-regular: #c5cfde;
  --el-text-color-secondary: #7f8da3;
  --el-border-color: #283448;
  --el-border-color-light: #222d3e;
  --el-border-color-lighter: #1d2736;
  background: #0a101a !important;
  border-left: 1px solid #283448;
  color: #e8edf7;
  box-shadow: -18px 0 48px rgba(0, 0, 0, 0.42);
}

.el-overlay .skill-drawer .el-drawer__header {
  min-height: 62px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 24px;
  border-bottom: 1px solid #202b3b;
  background: #0c131f;
  color: #f3f6fb;
}

.el-overlay .skill-drawer .el-drawer__title {
  color: #f3f6fb;
  font-size: 17px;
  font-weight: 700;
}

.el-overlay .skill-drawer .el-drawer__close-btn {
  color: #8290a5;
}

.el-overlay .skill-drawer .el-drawer__close-btn:hover {
  color: #ff9b4a;
}

.el-overlay .skill-drawer .el-drawer__body {
  padding: 22px 24px;
  background: #0a101a;
  color: #e8edf7;
  scrollbar-color: #354258 #0a101a;
  scrollbar-width: thin;
}

.el-overlay .skill-drawer .el-drawer__body::-webkit-scrollbar {
  width: 8px;
}

.el-overlay .skill-drawer .el-drawer__body::-webkit-scrollbar-track {
  background: #0a101a;
}

.el-overlay .skill-drawer .el-drawer__body::-webkit-scrollbar-thumb {
  border: 2px solid #0a101a;
  border-radius: 8px;
  background: #354258;
}

.el-overlay .skill-drawer .el-drawer__footer {
  padding: 14px 24px;
  border-top: 1px solid #202b3b;
  background: #0c131f;
}

.el-overlay .skill-drawer .el-form-item__label,
.el-overlay .skill-drawer .el-collapse-item__header {
  color: #cbd5e1;
}

.el-overlay .skill-drawer .el-collapse,
.el-overlay .skill-drawer .el-collapse-item__header,
.el-overlay .skill-drawer .el-collapse-item__wrap {
  border-color: #283448;
  background: transparent;
}

.el-overlay .skill-drawer .el-collapse-item__content {
  color: #c5cfde;
}

.el-overlay .skill-drawer .el-button:not(.el-button--primary) {
  border-color: #344158;
  background: #131c2b;
  color: #d4dce8;
}

.el-overlay .skill-drawer .el-button:not(.el-button--primary):hover {
  border-color: #ff8a38;
  color: #ffad70;
}

.el-overlay .skill-drawer .el-button--primary {
  border-color: #ff7417;
  background: #ff7417;
}

.el-overlay .skill-drawer .el-button--primary:hover {
  border-color: #ff8a38;
  background: #ff8a38;
}
</style>
